/* eslint-disable @nx/enforce-module-boundaries */
import {
  Box,
  Card,
  CardBody,
  Container,
  HStack,
  Heading,
  Image,
  Scrollable,
  // Scrollable,
  Stack,
  Text,
} from '@mybridge/ui';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from '@mybridge/ui';
import { useDispatch, useSelector } from 'react-redux';
import { trimStr } from 'v4/lib/commons';
import { AuthenticatedProviders } from 'v4/snippets/providers/authenticated';
import API from 'v4/store/common/api';
import { APIEndPoints } from 'v4/store/common/endPoint';
import {
  markReadPost,
} from 'v4/store/slices/blogSlice';
import { PostBoxFooter } from 'v4/components/post/footer';
import { PostBoxInsightPlate } from 'v4/components/post/insight-plate';
import { PostBoxFooterActions } from 'v4/components/post/footer-actions';
import { Divider } from '@mybridge/ui/divider';
import { PostBoxContext, usePostBoxContext } from 'v4/components/post/context';
import { PostCommentForm } from 'v4/snippets/dashboard/feed/post-box/post-comment/comment-form';
import { PostCommentRows } from 'v4/snippets/dashboard/feed/post-box/post-comment/comment-rows';
import  styles from '../../../snippets/search/layout/index.module.scss';

import { getSearchArticles } from 'v4/store/actions/global.data.actions';
import moment from 'moment';
import ArticleUserDetailsRight from 'v4/components/articles/article-user-details';
import { VStack } from '@chakra-ui/react';
import { isMobile } from 'react-device-detect';
import { SiteShortcutLinks } from 'v4/snippets/dashboard/site-shortcut-links';
import { DashboardMyCircle } from 'v4/snippets/dashboard/circle';
import { MainCopyrightsText } from 'v4/components/copyrights-text';
import { myBridgeData } from 'v4/components/header/status-dropdown/constant';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { PostBoxQuotePostModal } from 'v4/snippets/dashboard/feed/post-box/quote-post-modal';
import { PostBoxRepostModal } from 'v4/snippets/dashboard/feed/post-box/repost-modal';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
import { formatArticleContent } from 'v4/utils/utils';

const ArticlesDetailsPage = ({ showComments = true }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslator();
  const direction = useDirection();
  const query = router?.query?.slug;
  const descRef = useRef();
  const [eventAdmin, setEventAdmin] = useState(false);
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const { loggedIn } = useSelector((state) => state.user);
  const { globalNotifCount } = useSelector((state) => state.notifications);
  
  const [commentBoxVisible, setCommentBoxVisible] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const targetRef = useRef(null);
  const [randomThreeMessages, setRandomThreeMessages] = useState([]);
  const [blogDetails, setBlogDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const commentBoxRef = useRef(null);

  useEffect(() => {
    if (commentBoxVisible && commentBoxRef.current) {
      commentBoxRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [commentBoxVisible]);

  // Ensure we're on client side
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Fetch blog details on client side
  useEffect(() => {
    if (!isClient) return;
    
    // Wait for router to be ready
    if (!router.isReady) return;
    
    if (!query) {
      setLoading(false);
      return;
    }

    const fetchBlogDetails = async () => {
      try {
        setLoading(true);
        const obj = {
          url: `${APIEndPoints.GetBlogsDetails}${query}/detail`,
          isNoToken: true,
        };
        const data = await API(obj);
        setBlogDetails(data);
      } catch (error) {
        console.error('Error fetching blog details:', error);
        setBlogDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogDetails();
  }, [isClient, router.isReady, query]);

  // Handle hashtag and mention clicks - function-based approach
  const handleContentClick = useCallback((e) => {
    if (typeof window === 'undefined') return;
    
    try {
      // Check for hashtag clicks
      const hashtagTarget = e.target.closest?.('.hashtag-link');
      if (hashtagTarget) {
        e.preventDefault();
        e.stopPropagation();
        const hashtag = hashtagTarget.getAttribute('data-hashtag');
        if (hashtag) {
          router.push(`/g/?query=${encodeURIComponent(hashtag)}`).catch((err) => {
            console.error('Navigation error:', err);
          });
          return;
        }
      }

      // Check for mention clicks
      const mentionTarget = e.target.closest?.('.mention-link');
      if (mentionTarget) {
        e.preventDefault();
        e.stopPropagation();
        const mentionSlug = mentionTarget.getAttribute('data-mention-slug');
        if (mentionSlug) {
          router.push(`/p/${mentionSlug}`, undefined, { 
            shallow: true,
            scroll: false
          }).catch((err) => {
            console.error('Navigation error:', err);
          });
          return;
        }
      }
    } catch (error) {
      console.error('Error handling content click:', error);
    }
  }, [router]);

  // Ref callback to attach event listener to the container
  const setDescRef = useCallback((node) => {
    if (typeof window === 'undefined') return;
    
    // Remove old listener if ref changes
    if (descRef.current && descRef.current !== node) {
      descRef.current.removeEventListener('click', handleContentClick, true);
    }
    
    // Update ref
    descRef.current = node;
    
    // Attach new listener
    if (node) {
      node.addEventListener('click', handleContentClick, true);
    }
  }, [handleContentClick]);

  useEffect(() => {
      function getRandomThree(arr) {
        const shuffled = arr.sort(() => 0.5 - Math.random());
        return shuffled.slice(0, 3);
      }
      
      // Initialize with three random messages on mount
      setRandomThreeMessages(getRandomThree(myBridgeData));
    }, []);
  
  const extractText = (html) => {
    if (typeof window === 'undefined') return;
    const s = document.createElement('span');
    s.innerHTML = html;
    return s.textContent;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null, // viewport
        rootMargin: '0px', // no margin
        threshold: 0.2, // 50% of target visible
      }
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    // Clean up the observer
    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const interval = setTimeout(() => {
      if (
        loggedIn &&
        blogDetails?.author?.id !== userProfileInfo?.id &&
        !blogDetails?.readers?.includes(userProfileInfo?.id)
      ) {
        dispatch(markReadPost(blogDetails?.id));
      }
    }, 30000);
    return () => clearTimeout(interval);
  }, []);

  // const postModalLink = (id) => `?post=${id}`;
  const postModalLink = (id) => `/post/${id}`;

  let flatDescription = extractText(blogDetails?.description ?? '');
  if (flatDescription?.length) {
    flatDescription = trimStr(flatDescription, { length: 140 });
  }

  const postBoxContext = usePostBoxContext({
    post: blogDetails,
    initialFetchComments: showComments,
  });

  const { refetch, refetchComments, repostModal, quotePostModal } = postBoxContext ?? {};

  const refetchAll = () => {
    refetch?.();
    refetchComments?.();
  };

  const hasTrackedPageVisit = useRef(false);

// Add this function to get user slug
const getUserSlug = () => {
  return userProfileInfo?.slug || userProfileInfo?.username || null;
};

// Add this function to track article details page visit
const trackArticleDetailsPageVisit = () => {
  const userSlug = getUserSlug();
  if (!userSlug || !blogDetails) return;

  trackEvent(ANALYTICS_EVENTS.ArticleDetails, userSlug, {
    page_path: router.asPath,
    timestamp: new Date().toISOString()
  });
};

useEffect(() => {
  if (blogDetails && userProfileInfo && !hasTrackedPageVisit.current) {
    trackArticleDetailsPageVisit();
    hasTrackedPageVisit.current = true;
  }
}, [blogDetails, userProfileInfo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (descRef.current) {
        descRef.current.removeEventListener('click', handleContentClick, true);
      }
    };
  }, [handleContentClick]);

  // Don't render anything on server side
  if (!isClient) {
    return (
      <Box w="100%" h="100vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box w="100%" h="100vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!blogDetails) {
    return (
      <Box w="100%" h="100vh" display="flex" alignItems="center" justifyContent="center">
        <Text>Article not found</Text>
      </Box>
    );
  }

  return (
    <AuthenticatedProviders>
      <PostBoxContext.Provider
        value={{
          ...(postBoxContext ?? {}),
          commentBoxVisible,
          setCommentBoxVisible,
          showComments,
          postModalLink,
          repostModal,
          quotePostModal,
        }}
      >
        {blogDetails && (
          <Head>
            <title>
              {loggedIn && globalNotifCount ? `(${globalNotifCount})` : ''}
              {blogDetails?.title} | myBridge
            </title>
            <meta
              name="description"
              property="description"
              content={flatDescription}
            />
            <meta property="description" content={flatDescription} />
            <meta property="og:title" content={blogDetails?.title} />
            <meta property="og:description" content={flatDescription} />
            <meta property="og:image" content={blogDetails?.cover_image} />
            <link
              rel="canonical"
              href={`${typeof window !== 'undefined' && window.location.href}`}
            />
          </Head>
        )}

        <Container maxW="1350px" py={[0, 0]} pt={[5, ""]} width={isMobile? "100%" : "100%"}>
          <HStack alignItems="stretch" spacing={4} bg="white" minHeight="100vh">
            {/* Sidebar for SiteShortcutLinks */}
            <Stack
              display={['none', 'none', 'flex']}
              pos="sticky"
              top="64px"
              spacing={2}
              minH={isMobile?"100vh":""}
              borderBottom="1px solid #e0e0e094"
              height={isMobile?"500px":'350px'}
              zIndex="999"
            >
              <VStack
                spacing={2}
                justifyContent="space-between"
                height="100%"
                align="stretch"
                h="calc(100vh - 0px)"
                backgroundColor="rgb(255, 255, 255)"
              >
                <VStack w={!isMobile && "min-content"} spacing={2} align="stretch" h="100%" flex="1">
                  <SiteShortcutLinks />
                  <Box 
                //  className={styles.hideTitle}
                  >
                    <DashboardMyCircle h="300px" flex="1" />
                  </Box>
                </VStack>
              </VStack>
            </Stack>
            <Card top="64px" pt={4} bg="transparent" boxShadow="none" w={["100%","70%"]}>
              {/* {!isVisible ? (
                <Box
                  pos="sticky"
                  top={isMobile ? "calc(100vh - 80px)" : "calc(100vh - 70px)"}
                  bg={blogDetails?.blog_status!==1?"#F6F6F6":""}
                  zIndex="3"
                  minH="8"
                >
                  {blogDetails?.blog_status !== 1 && (
                    <PostBoxFooter>
                      <PostBoxInsightPlate />
                      <PostBoxFooterActions />
                      {commentBoxVisible ? (
                        <Box
                          ref={commentBoxRef}
                          px={4}
                          borderTop="1px solid"
                          borderColor="brandGray.500"
                        >
                          <PostCommentForm />
                        </Box>
                      ) : (
                        ''
                      )}
                    </PostBoxFooter>
                  )}
                </Box>
              ) : (
                ''
              )} */}

              <>
                <CardBody mt={!isVisible ? '-90px' : '0'} px={[0, '']}>
                  {blogDetails && blogDetails?.cover_image ? (
                    <Image
                      src={blogDetails?.cover_image}
                      mb={3}
                      width="100%"
                      alt={blogDetails?.title}
                      height={['', '380px']}
                      borderTopRightRadius="10px"
                      borderTopLeftRadius="10px"

                    />
                  ) : (
                    <></>
                  )}
                  <Heading
                    fontSize="32px"
                    fontWeight="600"
                    as="h1"
                    mb={2}
                    color="brandPrimary.500"
                    lineHeight="38.4px"
                  >
                    {blogDetails?.title}
                  </Heading>
                  <Stack spacing={3}>
                  <ArticleUserDetailsRight blogDetails={blogDetails} />
                    <HStack justifyContent="flex-start" gap={2} my={2}>
                      <Text>
                        {moment(blogDetails.updated_at)
                          .local()
                          .format('MMM DD, YYYY')}
                      </Text>
                    </HStack>

                    <Box
                      ref={setDescRef}
                      className="article-description"
                      color="brandGray.700"
                      dangerouslySetInnerHTML={{
                        // __html: blogDetails?.description,
                        __html: formatArticleContent(blogDetails?.description, blogDetails?.mention_users) 
                      }}
                    ></Box>
                  </Stack>
                </CardBody>
                {blogDetails?.blog_status!==1 && <Box 
                  pos="sticky"
                  bottom={0}
                  bg={blogDetails?.blog_status!==1?"#F6F6F6":""}
                  zIndex="3"
                  minH="8"
                  ref={targetRef}
                >
                  <PostBoxFooter>
                    <PostBoxInsightPlate />
                    <PostBoxFooterActions />
                    {commentBoxVisible ? (
                      <Box
                        px={4}
                        borderTop="1px solid"
                        borderColor="brandGray.500"
                      >
                        <PostCommentForm />
                      </Box>
                    ) : (
                      ''
                    )}
                    {/* {showComments ? (
                      <>
                        <Divider opacity={1} borderColor="gray.200" />
                        <Box px={4}>
                          <PostCommentRows
                            comments={blogDetails?.comments}
                            post={blogDetails}
                            showCommentsDefault={true}
                            refetch={refetchAll}
                          />
                        </Box>
                      </>
                    ) : (
                      ''
                    )} */}
                  </PostBoxFooter>
                  
                </Box>}
              </>
            </Card>
            {!isMobile && (
              <Scrollable
                pos="sticky"
                minH="100vh"
                top="67px"
              >
               
                  
            <Box
              flex={1}
              alignItems="center"
              justifyContent="center"
             
            >
              <Container
                mt={5}
                marginInlineStart="0px"
                marginInlineEnd={'0px'}
                maxW="355px"
                h={'100vh'}
                px={0}
                {...(direction === 'rtl') ? {ml: '20px'} : {mr: '20px'}}
                className={styles.rightSidePannel}
              >
             
       
               
              {randomThreeMessages?.map((content)=>(
                <>
          <Box
                    bg="#ECECEC"
                    boxShadow="0px 4px 4px 0px #00000040"
                    mt={4}
                    mx={2}
                    minH="175px"
                    border="1px solid #E4E6EB"
                    borderRadius={'16px'}
                  >
                    <Stack p={2}>
                      <Text color="#617898" fontSize="19px">
                        {t(content?.headingKey) || content?.heading}
                      </Text>
                      <VStack>
                        <Text color="#5B5B5B" fontSize="15px">
                          {t(content?.descriptionKey) || content?.description}
                        </Text>
                      </VStack>
                    </Stack>
                  </Box>

                </>
              ))}

    
                <Box display={['none', 'none', 'block']} mt={'30px'}>
                  <MainCopyrightsText alignment="center" />
                </Box>
              </Container>
            </Box>
              </Scrollable>
            )}
          </HStack>
        </Container>
        
        {repostModal?.isOpen ? <PostBoxRepostModal {...repostModal} /> : ''}
        {quotePostModal?.isOpen ? (
          <PostBoxQuotePostModal {...quotePostModal} />
        ) : (
          ''
        )}
        
      </PostBoxContext.Provider>
    </AuthenticatedProviders>
  );
};

export default ArticlesDetailsPage;
